import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { CodeInput } from './CodeInput';
import { DEFAULT_CODE_LENGTH, requestLink, verifyCode } from '~/lib/auth';
import { loginHolds, secondsLeft, waitMessageOver } from '~/lib/signIn';
import {
  mountTurnstile,
  STAFF_LOGIN_ACTION,
  turnstileSiteKey,
  type TurnstileHandle,
} from '~/lib/turnstile';

/**
 * Admin sign-in (codes ported from the Hype Forge Base, 2026-10-21). Step 1
 * asks for the email; the API sends an 8-digit code and a link. Step 2 takes
 * the code, which signs in THIS browser (the link signs in whichever browser
 * the mail app opens; /auth/callback handles it). When the site has Turnstile
 * on (GET /api/public/config gives a site key) one invisible widget runs on
 * both steps: its token goes with the request (action staff_login) and with
 * every code check (action staff_verify, ported from the Hype Forge Base,
 * 2026-10-22); the widget switches action with the step and is reset after
 * every attempt (lib/signIn.ts).
 *
 * Too many sign-in attempts (HTTP 429 with Retry-After; docs/sign-in.md,
 * "Sign-in limits"): the limited step shows "Too many sign-in attempts. Try
 * again in N minutes.", its button reads "Try again in N minutes" until the
 * wait is over, and "send a new code" waits it out too.
 */

type Phase = 'request' | 'sent';

const RESEND_SECONDS = 60;

/**
 * A wait the API asked for (HTTP 429 with Retry-After): the seconds left, counting down, and the function that starts
 * one. `left` is right on the render that starts the wait, so nothing in between reads it as over.
 */
function useWait(): [number, (seconds: number) => void] {
  const [until, setUntil] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const left = secondsLeft(until, now);
  useEffect(() => {
    if (left <= 0) return;
    const id = setTimeout(() => setNow(Date.now()), 1000);
    return () => clearTimeout(id);
  }, [left, now]);
  const start = useCallback((seconds: number) => {
    const t = Date.now();
    setNow(t);
    setUntil(t + seconds * 1000);
  }, []);
  return [left, start];
}

export function LoginPage() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('request');
  const [email, setEmail] = useState('');
  // Honeypot: hidden from people, filled by form-stuffing bots. The API answers a
  // filled one with the same { ok } it gives everyone and sends nothing.
  const [website, setWebsite] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // A code is being asked for: a code check waits it out (the check would switch the widget under the request's token
  // wait and send it without one, or check the old code against the new one and spend one of its tries).
  const submittingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [code, setCode] = useState('');
  // How many digits the emailed code has (the API says so in its answer).
  const [codeLength, setCodeLength] = useState(DEFAULT_CODE_LENGTH);
  const [checking, setChecking] = useState(false);
  // The last digit auto-submits; Enter right after must not send it twice.
  const checkingRef = useRef(false);
  const codeRef = useRef<HTMLInputElement>(null);
  // Bot protection on both steps: only when the API publishes a Turnstile site key.
  const turnstileEl = useRef<HTMLDivElement>(null);
  const turnstile = useRef<TurnstileHandle | null>(null);
  // Too many sign-in attempts: the API says how long to wait. Asking for a code (Send, and "send a new code") and
  // checking one are limited separately; each button waits out its own.
  const [requestWait, waitForRequest] = useWait();
  const [verifyWait, waitForVerify] = useWait();
  // The error on screen is a wait message: it goes once both waits are over.
  const [errorIsWait, setErrorIsWait] = useState(false);
  useEffect(() => {
    if (waitMessageOver(errorIsWait, requestWait, verifyWait)) {
      setError(null);
      setErrorIsWait(false);
    }
  }, [errorIsWait, requestWait, verifyWait]);

  useEffect(() => {
    let cancelled = false;
    void turnstileSiteKey().then(async (key) => {
      if (!key || cancelled || !turnstileEl.current) return;
      const handle = await mountTurnstile(turnstileEl.current, key, STAFF_LOGIN_ACTION);
      if (cancelled) handle?.destroy();
      else turnstile.current = handle;
    });
    return () => {
      cancelled = true;
      turnstile.current?.destroy();
      turnstile.current = null;
    };
  }, []);

  // 60-second resend cooldown.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCooldown]);

  async function sendCode(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    // Not while a code is being checked: a new code would retire the one in the check (and switch the widget under it).
    if (requestWait > 0 || checkingRef.current) return;
    setError(null);
    setErrorIsWait(false);
    submittingRef.current = true;
    setSubmitting(true);
    // The widget's token goes with the request; then it is switched to the code step, or reset for another try.
    const {
      error: err,
      codeLength: digits,
      retryAfter,
    } = await requestLink(email.trim(), website, turnstile.current);
    submittingRef.current = false;
    setSubmitting(false);
    if (retryAfter) {
      // "Too many sign-in attempts. Try again in 12 minutes." Send and "send a new code" wait it out.
      waitForRequest(retryAfter);
      setResendCooldown((s) => Math.max(s, retryAfter));
      setError(err);
      setErrorIsWait(true);
      return;
    }
    if (err) {
      setError(err);
      return;
    }
    setCodeLength(digits);
    setPhase('sent');
    setCode('');
    setResendCooldown(RESEND_SECONDS);
  }

  // The code signs in THIS browser. Same session path as the link's callback page.
  async function submitCode(value: string) {
    // Not while a new code is being asked for either (the other way round from sendCode's guard).
    if (checkingRef.current || submittingRef.current || value.length !== codeLength || verifyWait > 0) return;
    checkingRef.current = true;
    setChecking(true);
    setError(null);
    setErrorIsWait(false);
    // With Turnstile on, a token for the code step goes with the code; the widget is reset after every attempt.
    const {
      error: err,
      retryAfter,
      turnstileFailed,
    } = await verifyCode(email.trim(), value, turnstile.current);
    checkingRef.current = false;
    setChecking(false);
    if (retryAfter) {
      waitForVerify(retryAfter);
      setError(err);
      setErrorIsWait(true);
      return;
    }
    if (err) {
      // A failed Turnstile check spent no try: the typed code stays, ready to send again (and the words say press Sign
      // in, not reload, since a reload would drop this step; lib/signIn.ts, CODE_STEP_TURNSTILE_RETRY).
      setError(err);
      if (!turnstileFailed) setCode('');
      codeRef.current?.focus();
      return;
    }
    navigate('/', { replace: true });
  }

  // Which buttons a wait holds, and their words (lib/signIn.ts, tested in apps/admin/test/sign-in-wait.test.mjs).
  const holds = loginHolds({
    requestWait,
    verifyWait,
    resendCooldown,
    submitting,
    checking,
    hasEmail: email.trim() !== '',
    codeComplete: code.length === codeLength,
  });

  // Not while a code is being checked or a new one asked for: the answer would land on the email step (a check's token
  // wait would even end early once the widget switched, and the check go out without one).
  const stepBusy = checking || submitting;

  function back() {
    if (checkingRef.current || submitting) return;
    setPhase('request');
    if (!errorIsWait) setError(null);
    setCode('');
    turnstile.current?.use(STAFF_LOGIN_ACTION);
  }

  return (
    <div className="trc-login-screen">
      <div className="trc-login-card">
        <p className="trc-eyebrow">The River Crew</p>
        <h1 className="trc-osw trc-login-title">Admin</h1>

        {phase === 'request' && (
          <>
            <p className="trc-login-success">
              Enter your email and we'll send you a one-time sign-in code.
            </p>
            <form onSubmit={sendCode} className="trc-login-form">
              <label htmlFor="email" className="trc-login-label">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                autoFocus
                className="trc-login-input"
                aria-describedby={error ? 'login-error' : undefined}
              />
              {/* Honeypot (see above): off screen, skipped by the tab order and screen readers. */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                style={{ position: 'absolute', left: -9999, width: 1, height: 1, opacity: 0 }}
              />
              <button type="submit" disabled={holds.send.disabled} className="trc-login-button">
                {holds.send.label}
              </button>
              {error && (
                <p id="login-error" className="trc-login-error" role="alert">
                  {error}
                </p>
              )}
            </form>
          </>
        )}

        {phase === 'sent' && (
          <>
            <p className="trc-login-success">
              If <strong>{email.trim()}</strong> has access, {codeLength === 8 ? 'an' : 'a'}{' '}
              {codeLength}-digit sign-in code is on its way. Enter it here to sign in on this
              device. It works for 15 minutes.
            </p>
            <form
              className="trc-login-form"
              onSubmit={(e) => {
                e.preventDefault();
                void submitCode(code);
              }}
            >
              <label htmlFor="code" className="trc-login-label">
                Sign-in code
              </label>
              <CodeInput
                id="code"
                ref={codeRef}
                value={code}
                onChange={setCode}
                onComplete={(v) => void submitCode(v)}
                length={codeLength}
                busy={checking || submitting}
                autoFocus
                describedBy={error ? 'code-error' : undefined}
              />
              <button type="submit" disabled={holds.signIn.disabled} className="trc-login-button">
                {holds.signIn.label}
              </button>
              {error && (
                <p id="code-error" className="trc-login-error" role="alert">
                  {error}
                </p>
              )}
            </form>

            <div className="trc-login-tip">
              <strong>Didn't get it?</strong> Check your spam folder, since first-time emails
              sometimes land there.{' '}
              {holds.resend.offered ? (
                <>
                  Then{' '}
                  <button
                    type="button"
                    className="trc-login-linkbtn"
                    onClick={() => void sendCode()}
                    disabled={submitting || checking}
                  >
                    send a new code
                  </button>
                  . Only the newest code works.
                </>
              ) : (
                <span className="trc-login-muted">
                  You can ask for another in {holds.resend.inText}.
                </span>
              )}
            </div>

            <p className="trc-login-help">
              The email also has a sign-in link. It signs in whichever browser your email app opens,
              so on this device the code is the surer way.
            </p>
            <p className="trc-login-help">
              <button
                type="button"
                className="trc-login-linkbtn trc-login-linkbtn--muted"
                onClick={back}
                disabled={stepBusy}
              >
                ← Use a different email
              </button>
            </p>
          </>
        )}
        {/* The Turnstile widget (invisible unless Cloudflare wants a click); kept across both steps, its action following the step ("send a new code" switches it back for the request). */}
        <div ref={turnstileEl} className="trc-login-turnstile" />
      </div>
    </div>
  );
}
